import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pencil, Plus } from 'lucide-react'
import { formatarReais, Papel } from '@du/shared'
import {
  Badge, Button, Escolha, Input, Modal, SectionTitle, Select, Skeleton, Textarea, useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'
import { paraCentavos, paraTexto } from '../lib/dinheiro.js'
import { useSessao } from '../lib/sessao.js'

type Categoria = { id: string; nome: string; ordem: number; ativo: boolean; _count: { produtos: number } }
type Produto = {
  id: string
  categoriaId: string
  nome: string
  ingredientes: string | null
  precoCents: number
  fotoUrl: string | null
  ativo: boolean
  esgotado: boolean
  destaque: boolean
  categoria: { nome: string }
}

export function Cardapio() {
  const { usuario } = useSessao()
  const { avisar } = useAviso()
  const [categorias, setCategorias] = useState<Categoria[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [busca, setBusca] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [editando, setEditando] = useState<Partial<Produto> | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [preco, setPreco] = useState('')

  const podeEditar = usuario?.papel === Papel.ADMIN || usuario?.papel === Papel.GERENTE

  const carregar = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api<Categoria[]>('/v1/categorias'),
        api<Produto[]>('/v1/produtos'),
      ])
      setCategorias(c)
      setProdutos(p)
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui carregar o cardápio', detalhe: e instanceof ErroApi ? e.message : undefined })
    } finally {
      setCarregando(false)
    }
  }, [avisar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    return termo ? produtos.filter((p) => p.nome.toLowerCase().includes(termo)) : produtos
  }, [produtos, busca])

  const porCategoria = useMemo(() => {
    const mapa = new Map<string, Produto[]>()
    for (const p of filtrados) {
      const lista = mapa.get(p.categoriaId) ?? []
      lista.push(p)
      mapa.set(p.categoriaId, lista)
    }
    return mapa
  }, [filtrados])

  /**
   * Esgotar com um toque. Otimista: na correria do sábado, esperar o servidor
   * faz o atendente tocar de novo achando que não pegou — e o segundo toque
   * desfaria o primeiro.
   */
  const alternarEsgotado = async (p: Produto) => {
    const novo = !p.esgotado
    setProdutos((atuais) => atuais.map((x) => (x.id === p.id ? { ...x, esgotado: novo } : x)))
    try {
      await api(`/v1/produtos/${p.id}/esgotado`, { method: 'PATCH', corpo: { esgotado: novo } })
    } catch (e) {
      setProdutos((atuais) => atuais.map((x) => (x.id === p.id ? { ...x, esgotado: p.esgotado } : x)))
      avisar({ tom: 'erro', texto: `Não consegui marcar "${p.nome}"`, detalhe: e instanceof ErroApi ? e.message : undefined })
    }
  }

  const abrirEdicao = (p?: Produto) => {
    // Spread condicional: com exactOptionalPropertyTypes, passar
    // `categoriaId: undefined` é diferente de omitir o campo.
    setEditando(
      p ?? {
        ...(categorias[0] ? { categoriaId: categorias[0].id } : {}),
        ativo: true,
        destaque: false,
      },
    )
    setPreco(p ? paraTexto(p.precoCents) : '')
  }

  const salvar = async () => {
    if (!editando) return
    const centavos = paraCentavos(preco)
    if (centavos === null || centavos < 100) {
      avisar({ tom: 'erro', texto: 'Preço inválido', detalhe: 'Use o formato 18,90' })
      return
    }
    setSalvando(true)
    try {
      const corpo = {
        categoriaId: editando.categoriaId,
        nome: editando.nome,
        ingredientes: editando.ingredientes ?? null,
        precoCents: centavos,
        destaque: editando.destaque ?? false,
        ativo: editando.ativo ?? true,
      }
      if (editando.id) {
        await api(`/v1/produtos/${editando.id}`, { method: 'PATCH', corpo })
      } else {
        await api('/v1/produtos', { method: 'POST', corpo })
      }
      avisar({ tom: 'sucesso', texto: editando.id ? 'Produto atualizado' : 'Produto criado' })
      setEditando(null)
      await carregar()
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui salvar', detalhe: e instanceof ErroApi ? e.message : undefined })
    } finally {
      setSalvando(false)
    }
  }

  if (carregando) {
    return (
      <div className="pagina-gestao">
        <Skeleton altura={44} />
        <Skeleton altura={200} />
      </div>
    )
  }

  const esgotados = produtos.filter((p) => p.esgotado).length

  return (
    <div className="pagina-gestao">
      <div className="pagina-gestao__topo">
        <Input
          rotulo="Buscar produto"
          type="search"
          placeholder="X-Tudo, batata…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <div className="pagina-gestao__acoes">
          {esgotados > 0 ? <Badge tom="alerta">{esgotados} esgotado(s)</Badge> : null}
          {podeEditar ? (
            <Button icone={Plus} onClick={() => abrirEdicao()}>
              Novo produto
            </Button>
          ) : null}
        </div>
      </div>

      {categorias.map((c) => {
        const lista = porCategoria.get(c.id) ?? []
        if (lista.length === 0) return null
        return (
          <section key={c.id} className="bloco-gestao">
            <SectionTitle variante="vazado" tamanho="sm" acao={<Badge tom="neutro">{lista.length}</Badge>}>
              {c.nome}
            </SectionTitle>
            <ul className="lista-produtos">
              {lista.map((p) => (
                <li key={p.id} className={`linha-produto${p.esgotado ? ' linha-produto--esgotado' : ''}`}>
                  {p.fotoUrl ? (
                    <img src={p.fotoUrl} alt="" className="linha-produto__foto" loading="lazy" />
                  ) : (
                    <span className="linha-produto__sem-foto" aria-hidden />
                  )}
                  <div className="linha-produto__corpo">
                    <strong>
                      {p.nome}
                      {p.destaque ? <Badge tom="destaque">destaque</Badge> : null}
                      {!p.ativo ? <Badge tom="neutro">inativo</Badge> : null}
                    </strong>
                    {p.ingredientes ? <span>{p.ingredientes}</span> : null}
                  </div>
                  <span className="linha-produto__preco">{formatarReais(p.precoCents)}</span>
                  <div className="linha-produto__acoes">
                    {/* Esgotar fica à mão de todo mundo; editar, só de gestão. */}
                    <Button
                      variante={p.esgotado ? 'perigo' : 'ghost'}
                      tamanho="sm"
                      onClick={() => void alternarEsgotado(p)}
                    >
                      {p.esgotado ? 'Esgotado' : 'Tem'}
                    </Button>
                    {podeEditar ? (
                      <Button
                        variante="ghost"
                        tamanho="sm"
                        icone={Pencil}
                        onClick={() => abrirEdicao(p)}
                        aria-label={`Editar ${p.nome}`}
                      />
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )
      })}

      <Modal
        aberto={editando !== null}
        aoFechar={() => setEditando(null)}
        titulo={editando?.id ? `Editar ${editando.nome}` : 'Novo produto'}
        rodape={
          <>
            <Button variante="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => void salvar()} carregando={salvando}>
              Salvar
            </Button>
          </>
        }
      >
        {editando ? (
          <div className="form-modal">
            <Select
              rotulo="Categoria"
              value={editando.categoriaId ?? ''}
              onChange={(e) => setEditando({ ...editando, categoriaId: e.target.value })}
              opcoes={categorias.map((c) => ({ valor: c.id, rotulo: c.nome }))}
              required
            />
            <Input
              rotulo="Nome"
              value={editando.nome ?? ''}
              onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
              required
            />
            <Input
              rotulo="Preço"
              prefixo="R$"
              inputMode="decimal"
              placeholder="18,90"
              dica="Use vírgula para os centavos"
              value={preco}
              onChange={(e) => setPreco(e.target.value)}
              required
            />
            <Textarea
              rotulo="Ingredientes"
              maxLength={400}
              value={editando.ingredientes ?? ''}
              onChange={(e) => setEditando({ ...editando, ingredientes: e.target.value })}
            />
            <Escolha
              tipo="checkbox"
              nome="destaque"
              valor="1"
              rotulo="Mostrar em destaque"
              descricao="Aparece na vitrine do topo do cardápio"
              marcada={editando.destaque ?? false}
              onMudar={(m) => setEditando({ ...editando, destaque: m })}
            />
            <Escolha
              tipo="checkbox"
              nome="ativo"
              valor="1"
              rotulo="Visível no cardápio"
              descricao="Desmarque para esconder sem apagar o histórico"
              marcada={editando.ativo ?? true}
              onMudar={(m) => setEditando({ ...editando, ativo: m })}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
